import { nullIfString, stringIfNull } from '@/lib/string-utils';
import {
  FrequentTimeZones,
  getLearningLossLabel,
  getSessionSubstitutionRequestSummary,
  normalizeOriginalTeacherIssueReason,
} from '@airreading/common';
import {
  AdminSession,
  CoachSession,
  CoachStatus,
  learningLossSelectableSchema,
  learningLossSelectableValues,
  originalTeacherIssueSelectableSchema,
} from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PencilIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormInputTime } from '@/components/forms/form-input-time';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  buildNameLikeSelectOptions,
  buildUserSelectOptions,
  comboBoxOptionsOf,
  emptySelectOption,
  requiredMultiSelectOptions,
  selectOption,
} from '@/components/ui/multi-select-utils';

import {
  coachesTypeaheadSearch,
  getSubstituteCoachConflictsWithSession,
  studentsTypeaheadSearch,
  userTypeaheadSearch,
} from '@/lib/api-call-hooks';
import { getAdminSessions, getLearningPath, updateSessionDetails, updateSessionPeople } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { DefaultValues } from 'react-hook-form';
import { OverlappingSessionsModal } from './overlapping-sessions-modal';
import { FormTextArea } from '@/components/forms/form-text-area';
import {
  getOriginalTeacherIssueDropdownText,
  getOriginalTeacherIssueOptionClassName,
  getOriginalTeacherIssueOptionHint,
  orderedOriginalTeacherIssueOptions,
  shouldShowReasonField,
} from '@/lib/original-teacher-issue-utils';

const formSchema = z.object({
  supportAdmin: selectOption,
  sessionTeacher: selectOption,
  shouldSendConfirmationEmail: z.boolean().optional(),
  originalTeacher: selectOption,
  originalTeacherClassIssue: originalTeacherIssueSelectableSchema,
  reason: z.string().optional(),
  students: requiredMultiSelectOptions(),
  date: z.instanceof(Temporal.PlainDate),
  startTime: z.instanceof(Temporal.PlainTime),
  duration: z.enum(['30', '40']),
  lesson: selectOption,
  learningLoss: learningLossSelectableSchema,
  tnsNotes: z.string().nullable(),
});
type FormData = z.infer<typeof formSchema>;

export function EditSessionModal({ session }: { session: AdminSession }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutateAsync: updateSessionDetailsMutate } = useMutation({ mutationFn: updateSessionDetails });
  const { mutateAsync: updateSessionPeopleMutate } = useMutation({ mutationFn: updateSessionPeople });
  const adminTypeahead = userTypeaheadSearch(queryClient, { roles: ['school_admin', 'org_admin', 'air_admin'] });
  const coachTypeahead = coachesTypeaheadSearch(queryClient, { filters: { statuses: [CoachStatus.ready_to_teach] } });
  const schoolId = session.school?.id || session.studentGroupId;
  const studentTypeahead = studentsTypeaheadSearch(queryClient, { schools: [schoolId] });
  const { data: learningPath, isPending: isLoadingLearningPath } = useQuery(getLearningPath(session.learningPath.id));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCoachSession, setDialogCoachSession] = useState<CoachSession | null>(null);
  const [dialogSubmitProps, setDialogSubmitProps] = useState<SubmitProps<FormData> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { coachIdWhoIsTeaching: actualSessionTeacherId } = getSessionSubstitutionRequestSummary(session);
  const actualSessionTeacher =
    session.substitutionRequests.find((req) => req.substituteId === actualSessionTeacherId)?.substituteCoach ||
    session.coach;
  const actualOriginalTeacher = session.coach;

  const dt = session.startInstant.toZonedDateTimeISO(FrequentTimeZones.UsCentral);
  const defaultDate = dt.toPlainDate();
  const defaultStartTime = Temporal.PlainTime.from({ hour: dt.hour, minute: dt.minute });

  // Calculate duration from session's start and end times
  const durationMinutes = session.endInstant.until(session.startInstant).total('minutes');
  const defaultDuration = Math.abs(durationMinutes).toString() as '30' | '40';

  const defaultValues: DefaultValues<FormData> = {
    supportAdmin: session.supportAdmin ? buildUserSelectOptions(session.supportAdmin) : emptySelectOption,
    sessionTeacher: actualSessionTeacher ? buildUserSelectOptions(actualSessionTeacher) : emptySelectOption,
    shouldSendConfirmationEmail: false,
    originalTeacher: actualOriginalTeacher ? buildUserSelectOptions(actualOriginalTeacher) : emptySelectOption,
    originalTeacherClassIssue: stringIfNull(session.originalTeacherIssue),
    reason: session.originalTeacherIssueReason ?? '',
    students: session.students.map((s) => ({ label: s.fullNameOrEmail, value: s.id })),
    date: defaultDate,
    startTime: defaultStartTime,
    duration: defaultDuration,
    lesson: session.lesson ? buildNameLikeSelectOptions(session.lesson) : emptySelectOption,
    learningLoss: stringIfNull(session.learningLoss),
    tnsNotes: session.tnsNotes,
  };

  const lessonOptions = useMemo(
    () =>
      (learningPath?.learningPathLessons || [])
        ?.toSorted((a, b) => a.order - b.order)
        .map(({ lesson }) => lesson)
        .map(buildNameLikeSelectOptions) ?? [],
    [learningPath],
  );

  useEffect(() => {
    if (!dialogOpen) {
      setDialogCoachSession(null);
      setDialogSubmitProps(null);
    }
  }, [dialogOpen]);

  const onSubmit = async (submitProps: SubmitProps<FormData>) => {
    try {
      setIsSubmitting(true);
      const { formData, closeModal } = submitProps;
      const plainDate = formData.date;
      const startTime = formData.startTime;
      const durationMinutes = parseInt(formData.duration);
      const endTime = startTime.add({ minutes: durationMinutes });
      const timeZone = FrequentTimeZones.UsCentral;
      const start = plainDate.toZonedDateTime({ plainTime: startTime, timeZone });
      const end = plainDate.toZonedDateTime({ plainTime: endTime, timeZone });

      // Check if substitute coach has conflicts before updating
      const { coachHasConflicts, coachSessions } = await getSubstituteCoachConflictsWithSession({
        originalCoachId: formData.originalTeacher.value,
        substituteCoachId: formData.sessionTeacher.value,
        session,
        queryClient,
        start,
        end,
      });

      if (coachHasConflicts && coachSessions && coachSessions.length > 0 && !dialogCoachSession && !dialogSubmitProps) {
        closeModal();
        queryClient.invalidateQueries(getAdminSessions());

        setDialogCoachSession(coachSessions[0]);
        setDialogSubmitProps(submitProps);
        setDialogOpen(true);

        return;
      }

      await updateSessionPeopleMutate({
        sessionId: session.id,
        payload: {
          studentIds: formData.students.map((s) => s.value),
          coachId: formData.originalTeacher.value,
          supportAdminId: formData.supportAdmin.value || undefined,
          originalTeacherIssue: nullIfString(formData.originalTeacherClassIssue),
          originalTeacherIssueReason: shouldShowReasonField(formData.originalTeacherClassIssue)
            ? normalizeOriginalTeacherIssueReason(formData.reason)
            : null,
          substituteCoachId: formData.sessionTeacher.value,
          shouldSendConfirmationEmail: formData.shouldSendConfirmationEmail,
          shouldSendUpdateConfirmationEmail: formData.shouldSendConfirmationEmail,
          learningLoss: nullIfString(formData.learningLoss),
          tnsNotes: formData.tnsNotes,
        },
      });

      await updateSessionDetailsMutate({
        id: session.id,
        data: {
          status: session.status,
          start: start.toInstant().toString(),
          end: end.toInstant().toString(),
          lessonId: formData.lesson.value,
          shouldSendEmail: !formData.shouldSendConfirmationEmail, // Don't send update email if substitution email will be sent
        },
      });

      toast({ title: 'Session updated successfully' });
      closeModal();
      queryClient.invalidateQueries(getAdminSessions());
      setDialogOpen(false);
    } catch (error) {
      const errorMessage =
        airServerErrorFrom(error)?.userFacingMessage ??
        (error instanceof Error ? error.message : 'There was an error updating the session. Please try again.');

      toast({
        title: 'Failed to update session',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <FormModal
        modalTitle="Edit session"
        formSchema={formSchema}
        onSubmit={onSubmit}
        confirmBtnLabel="Save"
        cancelBtnLabel="Cancel"
        trigger={
          <Button variant="ghost" size="icon">
            <PencilIcon size={16} />
          </Button>
        }
        defaultValues={defaultValues}
        resetOnDefaultValuesChange
      >
        {({ form }) => {
          const sessionTeacher = form.watch('sessionTeacher');
          const originalTeacher = form.watch('originalTeacher');
          const showConfirmSub =
            !!sessionTeacher?.value && !!originalTeacher?.value && sessionTeacher.value !== originalTeacher.value;
          const watchedOriginalTeacherIssue = form.watch('originalTeacherClassIssue');
          const showReasonField = shouldShowReasonField(watchedOriginalTeacherIssue);
          return (
            <div className="space-y-4 mt-4">
              <FormComboBox
                form={form}
                name="supportAdmin"
                label="Support admin"
                triggerProps={{ className: 'w-full' }}
                {...comboBoxOptionsOf(adminTypeahead)}
              />
              <div className={`grid grid-cols-${showConfirmSub ? '3' : '2'} gap-4 items-end`}>
                <FormComboBox
                  form={form}
                  name="sessionTeacher"
                  label="Session teacher"
                  triggerProps={{ className: 'w-full' }}
                  {...comboBoxOptionsOf(coachTypeahead)}
                />
                <FormComboBox
                  form={form}
                  name="originalTeacher"
                  label="Original teacher"
                  triggerProps={{ className: 'w-full' }}
                  {...comboBoxOptionsOf(coachTypeahead)}
                />
                <div>
                  {showConfirmSub && (
                    <FormCheckbox form={form} name="shouldSendConfirmationEmail" label="Send confirmation email?" />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormSingleSelect
                  form={form}
                  name="originalTeacherClassIssue"
                  label="Original teacher class issue"
                  options={orderedOriginalTeacherIssueOptions}
                  renderOption={(issue) => {
                    const optionHint = getOriginalTeacherIssueOptionHint(issue);
                    return (
                      <div>
                        <div>{getOriginalTeacherIssueDropdownText(issue)}</div>
                        {optionHint ? <div className="text-muted-foreground">{optionHint}</div> : null}
                      </div>
                    );
                  }}
                  renderOptionSelected={getOriginalTeacherIssueDropdownText}
                  optionClassName={getOriginalTeacherIssueOptionClassName}
                  triggerProps={{ className: 'w-full [&>span]:block [&>span]:text-left' }}
                />
                <FormSingleSelect
                  form={form}
                  name="learningLoss"
                  label="Learning loss"
                  options={learningLossSelectableValues}
                  renderOption={getLearningLossLabel}
                  triggerProps={{ className: 'w-full' }}
                />
              </div>
              {showReasonField ? (
                <FormTextArea
                  form={form}
                  name="reason"
                  label="Reason"
                  textAreaProps={{
                    className: 'w-full resize-none max-h-24',
                    placeholder: 'Please enter a reason',
                    rows: 3,
                  }}
                />
              ) : null}
              <FormTextArea
                form={form}
                name="tnsNotes"
                label="TNS notes"
                textAreaProps={{ className: 'w-full resize-none' }}
              />
              <FormMultiSelect
                form={form}
                name="students"
                label="Students"
                multipleSelectorProps={{
                  onSearch: studentTypeahead,
                  className: 'w-full',
                  placeholder: 'Search and select students',
                }}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormInputDate form={form} name="date" label="Date" triggerButtonProps={{ className: 'w-full' }} />
                <FormInputTime
                  form={form}
                  name="startTime"
                  label="Start time (CT)"
                  triggerButtonProps={{ className: 'w-full' }}
                  step={Temporal.Duration.from({ minutes: 1 })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormSingleSelect
                  form={form}
                  name="duration"
                  label="Class duration"
                  options={['30', '40']}
                  renderOption={(value) => `${value} minutes`}
                  triggerProps={{ className: 'w-full' }}
                />
                <FormComboBox
                  form={form}
                  name="lesson"
                  label="Lesson"
                  isLoadingKnownOptions={isLoadingLearningPath}
                  triggerProps={{ className: 'w-full' }}
                  {...comboBoxOptionsOf(lessonOptions)}
                />
              </div>
            </div>
          );
        }}
      </FormModal>
      {dialogSubmitProps && dialogCoachSession && (
        <OverlappingSessionsModal
          coachSession={dialogCoachSession}
          open={dialogOpen}
          setOpen={setDialogOpen}
          onConfirm={() => onSubmit(dialogSubmitProps)}
          loading={isSubmitting}
        />
      )}
    </>
  );
}
